// Common utility functions used across components

/**
 * Returns true only for Cloudinary-hosted HTTPS image URLs.
 * Prevents arbitrary external URLs from being loaded as images, which
 * would allow remote tracking pixels, third-party content injection,
 * and deceptive branding.
 */
export const isTrustedLogoUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === "https:" &&
      parsed.hostname === "res.cloudinary.com"
    );
  } catch {
    return false;
  }
};

// Duration calculation utility
export const calculateDuration = (
  checkIn: string,
  checkOut?: string,
  durationMinutes?: number,
) => {
  if (durationMinutes !== undefined) {
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    if (days > 0) {
      return {
        text: `${days}d ${remainingHours}h ${minutes}m`,
        isOngoing: false,
      };
    }
    if (hours > 0) {
      return { text: `${hours}h ${minutes}m`, isOngoing: false };
    }
    return { text: `${minutes}m`, isOngoing: false };
  }

  if (!checkOut) {
    return { text: "Ongoing", isOngoing: true };
  }

  // Fallback calculation
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const diff = end.getTime() - start.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (days > 0) {
    return { text: `${days}d ${remainingHours}h`, isOngoing: false };
  }
  return { text: `${hours}h`, isOngoing: false };
};

// Format duration in minutes to readable string
export const formatDuration = (minutes: number): string => {
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  return `${days}d ${hours}hr ${mins}min`;
};

// Transform API data to Vehicle interface
export const transformApiDataToVehicle = (apiData: any[]) => {
  return apiData.map((item: any) => ({
    id: item._id,
    tokenId: item.tokenNumber,
    vehicleNumber: item.vehicleId?.vehicleNumber || "N/A",
    ownerName: item.assignmentId?.ownerId?.name || "Owner",
    ownerMobile: item.assignmentId?.ownerId?.mobile || "N/A",
    driverName: item.assignmentId?.driverId?.name || "Driver",
    vehicleType: item.vehicleId?.vehicleType || "N/A",
    checkInDate: item.inTime,
    checkInTime: new Date(item.inTime).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    }),
    checkOutDate: item.outTime || undefined,
    checkOutTime: item.outTime
      ? new Date(item.outTime).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : undefined,
    rate: item.rate,
    totalAmount: item.finalAmount,
    status: item.status as "IN" | "OUT",
    durationMinutes: item.durationMinutes,
  }));
};

// Generate PDF filename
export const generatePdfFileName = (
  vehicleNumber: string,
  tokenNumber?: string | number,
  tag: string = 'ARCHIVE',
): string => {
  const token = tokenNumber || Date.now();
  return `${vehicleNumber}_${tag}_${token}.pdf`;
};

// Validate mobile number
export const isValidMobile = (mobile: string): boolean => {
  return /^[0-9]{10}$/.test(mobile);
};

const CURRENCY_LOCALE_MAP: Record<string, string> = {
  INR: "en-IN",
  USD: "en-US",
  EUR: "en-IE",
  GBP: "en-GB",
  AUD: "en-AU",
  AED: "en-AE",
  SGD: "en-SG",
};

interface CurrencyFormatOptions {
  currency?: string;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

// Format currency
export const formatCurrency = (
  amount: number,
  options: CurrencyFormatOptions = {},
): string => {
  const currency = (options.currency || "INR").toUpperCase();
  const locale = options.locale || CURRENCY_LOCALE_MAP[currency] || "en-IN";

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 0,
    })
      .format(amount)
      .replace(/\u00A0/g, " ");
  } catch {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 0,
    }).format(amount);
  }
};

// Date formatting utilities

// YYYY-MM-DD in the operator's local calendar. Avoid toISOString() here — it
// silently shifts to UTC and lops off a day for non-UTC tenants (e.g. midnight
// IST → previous date).
export const localDateStr = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Convert a YYYY-MM-DD form value into an ISO timestamp anchored at midnight
// in the operator's local timezone. Plain `new Date("2026-04-01").toISOString()`
// would anchor at UTC midnight (= 5:30 AM IST), which silently shifts pass
// validity boundaries.
export const localDateToISO = (dateStr: string): string => {
  const [y, m, day] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, day).toISOString();
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-IN");
};

export const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString("en-IN", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

export const formatTime = (dateString: string): string => {
  return new Date(dateString).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Relative time formatting (e.g., "5m ago", "2h ago")
export const formatRelativeTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Full date-time with month name (e.g., "22 Mar 2026, 10:30 AM")
export const formatFullDateTime = (timestamp: string): string => {
  return new Date(timestamp).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const TextFormater = (effect: string): string => {
  return effect
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c: string) => c.toUpperCase());
};